import { useCallback, useEffect, useMemo } from "react";
import { Button, DatePicker, Drawer, Form, InputNumber } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import type { CostPricePayload, CostPriceType } from "@src/types";
import { useCostPriceCreate, useCostPriceUpdate } from "@src/queries";

export interface CostPriceDrawerProps {
    open: boolean;
    merchantProductId: string;
    costPrice?: CostPriceType | null;
    onClose: () => void;
}

interface CostPriceFormValues {
    cost_price: number;
    active_from: Dayjs;
}

const DRAWER_TITLE: Record<"create" | "edit", string> = {
    create: "Добавить себестоимость",
    edit: "Редактировать себестоимость",
};

const DATE_TIME_FORMAT = "DD.MM.YYYY HH:mm";

function mapToPayload(values: CostPriceFormValues): CostPricePayload {
    const activeFrom = values.active_from ?? dayjs();
    return {
        cost_price: values.cost_price,
        active_from: activeFrom.toISOString(),
    };
}

export function CostPriceDrawer({ open, merchantProductId, costPrice, onClose }: CostPriceDrawerProps) {
    const [form] = Form.useForm<CostPriceFormValues>();
    const isEditMode = Boolean(costPrice);

    const createMutation = useCostPriceCreate(merchantProductId);
    const updateMutation = useCostPriceUpdate(merchantProductId);

    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!open) {
            form.resetFields();
            return;
        }

        if (costPrice) {
            form.setFieldsValue({
                cost_price: costPrice.cost_price,
                active_from: dayjs(costPrice.active_from),
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                active_from: dayjs(),
            } as Partial<CostPriceFormValues>);
        }
    }, [costPrice, form, open]);

    const handleClose = useCallback(() => {
        if (isSubmitting) return;
        form.resetFields();
        onClose();
    }, [form, isSubmitting, onClose]);

    const handleSubmit = useCallback(
        async (values: CostPriceFormValues) => {
            const payload = mapToPayload(values);
            if (isEditMode && costPrice) {
                await updateMutation.mutateAsync(
                    { costPriceId: costPrice.id, payload },
                    {
                        onSuccess: () => {
                            form.resetFields();
                            onClose();
                        },
                    },
                );
                return;
            }

            await createMutation.mutateAsync(payload, {
                onSuccess: () => {
                    form.resetFields();
                    onClose();
                },
            });
        },
        [costPrice, createMutation, form, isEditMode, onClose, updateMutation],
    );

    const title = useMemo(() => DRAWER_TITLE[isEditMode ? "edit" : "create"], [isEditMode]);

    return (
        <Drawer
            title={title}
            open={open}
            onClose={handleClose}
            destroyOnHidden
            maskClosable={!isSubmitting}
            extra={
                <Button
                    type="primary"
                    loading={isSubmitting}
                    onClick={() => form.submit()}
                    disabled={isSubmitting}
                >
                    Сохранить
                </Button>
            }
        >
            <Form<CostPriceFormValues>
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
                requiredMark="optional"
            >
                <Form.Item
                    label="Себестоимость (₸)"
                    name="cost_price"
                    rules={[
                        { required: true, message: "Укажите себестоимость" },
                        {
                            type: "number",
                            min: 0,
                            message: "Себестоимость не может быть отрицательной",
                        },
                    ]}
                >
                    <InputNumber
                        className="w-full"
                        min={0}
                        placeholder="Введите себестоимость"
                        controls={false}
                    />
                </Form.Item>

                <Form.Item
                    label="Дата начала действия"
                    name="active_from"
                    rules={[{ required: true, message: "Укажите дату начала действия" }]}
                >
                    <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format={DATE_TIME_FORMAT}
                        className="w-full"
                    />
                </Form.Item>
            </Form>
        </Drawer>
    );
}

export default CostPriceDrawer;
